import * as React from 'react';

import { Form, Formik } from 'formik';
import { connect } from 'react-redux';
import * as yup from 'yup';

import Button from '@components/legos/Button';
import Box from '@components/legos/primitives/Box';
import Header from '@components/legos/primitives/Header';
import { ThunkDispatch } from '@src/domain';
import { updatePassword } from '@src/domain/User/UserActions';
import { RouteChildrenProps } from 'react-router';
import FormField from '../legos/forms/FormField';
import { SubmitButton, CancelButton } from '../legos/forms/FormButton';
import FormStatus, { IFormStatusObject } from '../legos/forms/FormStatus';
import CustomFormField from '../legos/forms/CustomFormField';
import { TextInput } from '../legos/inputs/TextInput';
import Flex from '../legos/primitives/Flex';
import PasswordStrengthChecker from './LoggedOut/PasswordStrengthChecker';

const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  changePassword: (values: typeof ChangePasswordDefaultValues) =>
    dispatch(updatePassword(values.newPassword, values.oldPassword)),
});

const ChangePasswordValidationSchema = yup.object().shape({
  newPassword: yup.string().label('Password')
    .min(6)
    .max(256)
    .required(),
  newPasswordConfirmation: yup.string()
    .oneOf([yup.ref('newPassword')], 'Passwords must match')
    .required('Please retype your new password here'),
  oldPassword: yup.string().required('Old password is required'),
});

const ChangePasswordDefaultValues = {
  newPassword: '',
  newPasswordConfirmation: '',
  oldPassword: '',
};

type ChangePasswordProps =
  ReturnType<typeof mapDispatchToProps>
  & RouteChildrenProps
  & { onRequestClose: () => void};

const ChangePassword: React.FC<ChangePasswordProps> = ({ changePassword, onRequestClose }) => (
  <Box minWidth='300px'>
    <Header mb={4}>Change Password</Header>
    <Formik
      initialValues={ChangePasswordDefaultValues}
      onSubmit={async (values, { resetForm, setStatus, setSubmitting }) => {
        try {
          await changePassword(values);
          setSubmitting(false);
          resetForm();
          setStatus({
            message: 'Password reset succeeded',
            type: 'success',
          } as IFormStatusObject);
        } catch (e) {
          setSubmitting(false);
          setStatus({
            message: e.response.status === 400 ? 'Old password is incorrect' : e.response.message || e.message,
            type: 'error',
          } as IFormStatusObject);
        }
      }}
      validationSchema={ChangePasswordValidationSchema}
    >
      {({ status, dirty, errors }) => (
        <Form>
          <CustomFormField
            label='New Password'
            name='newPassword'
            render={({ form, field }, errorState) => (
              <Flex flexDirection='column' style={{ position: 'relative' }}>
                <PasswordStrengthChecker error={errorState} password={form.values['newPassword']} />
                <TextInput error={errorState} {...field} id={field.name} type='password' />
              </Flex>
            )}
          />
          <FormField
            label='Confirm New Password'
            name='newPasswordConfirmation'
            placeholder='re-enter password'
            type='password'
          />
          <FormField
            label='Old Password'
            name='oldPassword'
            type='password'
          />
          <FormStatus />
          {(status && status.type === 'success' && !dirty && !Object.keys(errors).length)
            ? <React.Fragment>
              <SubmitButton>Save</SubmitButton>
              <Button
                ml={2}
                onClick={onRequestClose}
                type='button'
              >
                Done
              </Button>
            </React.Fragment>
            : <React.Fragment>
              <SubmitButton>Save</SubmitButton>
              <CancelButton onClick={onRequestClose}>Cancel</CancelButton>
            </React.Fragment>
          }
        </Form>)}
    </Formik>
  </Box>
);

export default connect(null, mapDispatchToProps)(ChangePassword);
